import { createBrowserClient } from '@supabase/ssr'

const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL!
const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!

// Production environment detection
const isProduction = typeof window !== 'undefined' && 
  (window.location.hostname === 'alphatank.ai' || 
   window.location.hostname === 'www.alphatank.ai');

// Add error handling for common auth flow errors
if (typeof window !== 'undefined') {
  // Save original console.error to maintain normal error logging for other errors
  const originalConsoleError = console.error;
  
  // Override console.error to filter out specific auth errors
  console.error = function(...args) {
    // Check if this is an auth error we want to suppress
    const errorString = args.join(' ');
    const isAuthSessionMissingError = 
      (errorString.includes('AuthSessionMissing') || 
      errorString.includes('Auth session missing'));
    
    // If in an auth-related URL and it's a session missing error, suppress it
    const isInAuthFlow = window.location.pathname.includes('/auth/') || 
      window.location.pathname === '/' || 
      window.location.pathname.includes('/sign-up');
      
    if (isAuthSessionMissingError && isInAuthFlow) {
      // Replace with a debug log instead of an error
      console.debug('Auth flow info: Session not yet available (expected during auth flow)');
      return;
    }
    
    // For all other errors, use the original console.error
    originalConsoleError.apply(console, args);
  };
  
  // Add automatic recovery for auth issues in production
  if (isProduction) {
    // Handle unexpected errors that might create blank screens
    window.addEventListener('error', function(event) {
      if (event.error && 
          (event.error.message?.includes('auth') || 
           event.error.message?.includes('token') || 
           event.error.message?.includes('session'))) {
        console.warn('Auth-related error detected, attempting recovery:', event.error.message);
        
        // Check if we're on a protected route
        if (window.location.pathname.includes('/dashboard')) {
          // Clear problematic auth state and redirect to sign-in
          const authErrorCount = parseInt(localStorage.getItem('auth_error_count') || '0');
          
          if (authErrorCount > 3) {
            // Too many errors, force sign out
            console.warn('Too many auth errors, forcing sign out');
            localStorage.clear();
            sessionStorage.clear();
            
            // Clear all cookies
            document.cookie.split(";").forEach(function(c) { 
              document.cookie = c.replace(/^ +/, "").replace(/=.*/, 
                "=;expires=" + new Date().toUTCString() + ";path=/"); 
            });
            
            window.location.href = '/auth/sign-in?recovery=needed';
          } else {
            // Increment error count and reload
            localStorage.setItem('auth_error_count', (authErrorCount + 1).toString());
            window.location.reload();
          }
        }
      }
    });
  }
}

// Only override fetch in browser environments
if (typeof window !== 'undefined') {
  const originalFetch = window.fetch;
  window.fetch = async (input, init) => {
    try {
      // Convert input to URL object if it's a string, handling relative URLs properly
      const url = typeof input === 'string' 
        ? new URL(input, window.location.origin) 
        : input instanceof URL 
          ? input 
          : new URL(input.url, window.location.origin);
      
      // Check if this is a request to the Supabase auth API for invitation verification
      if (url.toString().includes('/auth/v1/invitations/invite_code/verify')) {
        console.log('Intercepting invite code verification request', url.toString());
        
        // Extract the code parameter
        const code = url.searchParams.get('code');
        
        // Redirect to our custom API
        const newUrl = `/api/invitations/verify?code=${code}`;
        console.log('Redirecting to', newUrl);
        
        return originalFetch(newUrl, init);
      }
      
      // Also intercept token endpoint requests
      if (url.toString().includes('/auth/v1/token')) {
        console.log('Intercepting token request', url.toString());
        
        // For token requests, preserve all the original query parameters
        const newUrl = `/api/auth/v1/token${url.search}`;
        
        // Log important information for debugging
        console.log('Token request details:', {
          originalUrl: url.toString(),
          newUrl: newUrl,
          searchParams: Object.fromEntries(url.searchParams.entries()),
          method: init?.method || 'GET'
        });
        
        // Special handling for invite verification token requests
        if (url.searchParams.get('grant_type') === 'oace') {
          console.log('Special handling for invite code verification token');
          
          // After getting the token, redirect to sign-up page with the code
          const response = await originalFetch(newUrl, init);
          
          // Check if the response is ok
          if (response.ok) {
            // Get the invite code from the URL if present
            const inviteCode = url.searchParams.get('code') || '';
            
            if (inviteCode) {
              // We'll handle the redirect in the calling code
              console.log(`Token for invite code ${inviteCode} successfully obtained`);
            }
          }
          
          return response;
        }
        
        return originalFetch(newUrl, init);
      }
      
      // For all other requests, use the original fetch
      return originalFetch(input, init);
    } catch (error) {
      console.error('Error in fetch interceptor:', error);
      // Fall back to original fetch if our interception fails
      return originalFetch(input, init);
    }
  };
}

// Create the Supabase client with optimized configuration
export const supabase = createBrowserClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    // Add a debug mode during auth flows to minimize errors
    debug: isProduction ? false : true
  },
  cookies: {
    // Required for SSR
    get(name) {
      if (typeof window === 'undefined') return '';
      const cookie = document.cookie
        .split('; ')
        .find((row) => row.startsWith(`${name}=`));
      return cookie ? cookie.split('=')[1] : '';
    },
    set(name, value, options) {
      if (typeof window === 'undefined') return;
      document.cookie = `${name}=${value}; path=${options?.path || '/'}; max-age=${options?.maxAge || 315360000}; SameSite=${options?.sameSite || 'Lax'}`;
    },
    remove(name, options) {
      if (typeof window === 'undefined') return;
      document.cookie = `${name}=; path=${options?.path || '/'}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    }
  },
  // Additional global error handler for Supabase client
  global: {
    fetch: (...args) => {
      // Add recovery mechanism for auth requests in production
      if (isProduction) {
        return fetch(...args).catch(error => {
          console.error('Supabase fetch error:', error);
          // For auth-specific errors, try to handle gracefully
          if (error.message?.includes('Failed to fetch') || 
              error.message?.includes('NetworkError')) {
            console.warn('Network error in Supabase fetch, will retry on next page load');
            // Mark for retry
            if (typeof window !== 'undefined') {
              localStorage.setItem('supabase_fetch_retry', 'true');
            }
          }
          throw error;
        });
      }
      return fetch(...args);
    }
  }
})

// Add a helper method to get the current user securely
export const getCurrentUser = async () => {
  try {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  } catch (error) {
    console.error('Error getting current user:', error);
    // In production, try recovery mechanisms
    if (isProduction && typeof window !== 'undefined') {
      // Check if we should attempt a session recovery
      const recoveryAttempts = parseInt(localStorage.getItem('auth_recovery_attempts') || '0');
      if (recoveryAttempts < 3) {
        console.log('Attempting auth recovery (attempt ' + (recoveryAttempts + 1) + ')');
        localStorage.setItem('auth_recovery_attempts', (recoveryAttempts + 1).toString());
        
        // Wait and retry
        await new Promise(resolve => setTimeout(resolve, 1000));
        return supabase.auth.getUser().then(res => res.data.user).catch(() => null);
      }
    }
    return null;
  }
}

// Types for our database tables
export type Profile = {
  id: string
  username: string
  display_name?: string
  avatar_url?: string
  bio?: string
  public_picks: boolean
  reference_score: number
  badge_level?: string
  role: string
  created_at: string
  updated_at: string
  deleted_at?: string
}

export type Post = {
  id: number
  author_id: string
  crypto_id?: number
  title?: string
  content?: string
  post_type?: string
  is_published: boolean
  scheduled_for?: string | null
  created_at: string
  updated_at: string
}

export type Cryptocurrency = {
  id: number
  name: string
  symbol: string
  description?: string
  logo_url?: string
  website?: string
  is_recommended: boolean
  created_at: string
  updated_at: string
}

export type Follower = {
  id: number
  follower_id: string
  followed_id: string
  created_at: string
}

export type PortfolioPick = {
  id: number
  user_id: string
  post_id: number
  added_at: string
}

export type SubscriptionPlan = {
  id: number
  provider_id: string
  price: number
  currency: string
  frequency: string
  commission_rate: number
  created_at: string
  updated_at: string
}

export type Subscription = {
  id: number
  subscriber_id: string
  plan_id: number
  paid_amount: number
  commission_amount: number
  subscription_date: string
  next_billing_date?: string
  status: string
  created_at: string
  updated_at: string
}

export type Tip = {
  id: number
  sender_id: string
  receiver_id: string
  amount: number
  currency?: string
  note?: string
  created_at: string
}

export type Invitation = {
  id: number
  invite_code: string
  invited_by: string
  recipient_email?: string
  used: boolean
  used_at?: string
  expiration_date?: string
  created_at: string
}

export type ActivityLog = {
  id: number
  user_id?: string
  action: string
  details?: {
    type?: string
    target_id?: string | number
    metadata?: Record<string, string | number | boolean | null>
  }
  created_at: string
}

export type UserWallet = {
  id: number
  user_id: string
  wallet_type: string
  wallet_address: string
  created_at: string
  updated_at: string
} 